'use client';
import { Skeleton } from '../Skeleton';
import styles from './video.module.sass';
import cn from 'classnames';
import { FontIcon } from '../FontIcon';
import { useRef, useState } from 'react';

interface BaseProps extends React.HTMLAttributes<HTMLDivElement> {
    isLoading?: boolean;
}

interface LoadingProps extends BaseProps {
    isLoading: true;
    poster?: string;
    video?: string | string[];
    autoplay?: boolean;
    controls?: boolean;
    loop?: boolean;
    muted?: boolean;
    message?: string;
}

interface LoadedProps extends BaseProps {
    isLoading?: false;
    poster: string;
    video?: string | string[];
    autoplay?: boolean;
    controls?: boolean;
    loop?: boolean;
    muted?: boolean;
    message?: string;
}

export type Html5VideoProps = LoadingProps | LoadedProps;

export const Html5Video = ({
    poster,
    video,
    loop = false,
    autoplay = false,
    controls = false,
    muted = false,
    message,
    isLoading,
    ...props
}: Html5VideoProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isError, setIsError] = useState(false);

    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const getMimeType = (url: string) => {
        const extension = url.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'mp4':
                return 'video/mp4';
            case 'webm':
                return 'video/webm';
            case 'ogg':
                return 'video/ogg';
            case 'mov':
                return 'video/quicktime';
            case 'mkv':
                return 'video/x-matroska';
            default:
                return 'video/mp4';
        }
    };

    const videoSources = Array.isArray(video) ? video : [video];

    if (isLoading) {
        return (
            <div {...props} className={cn(styles.video5, props.className)}>
                <Skeleton absolute />
            </div>
        );
    }

    return (
        <div
            {...props}
            className={cn(styles.video5, isPlaying && styles.playing, isError && styles.error, props.className)}
        >
            {video ? (
                <>
                    <video
                        ref={videoRef}
                        poster={poster}
                        controls={controls}
                        autoPlay={autoplay}
                        loop={loop}
                        muted={muted}
                        onEnded={() => setIsPlaying(false)}
                        onError={() => setIsError(true)}
                    >
                        {videoSources.map((src, index) =>
                            src ? <source key={index} src={src} type={getMimeType(src)} /> : null
                        )}
                        <p className={styles.message}>{message || 'Your browser does not support HTML5 video.'}</p>
                    </video>
                    {!isError && (
                        <div className={styles.play} onClick={handlePlayPause}>
                            <FontIcon name="play" size="x-large" />
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.poster} style={{ backgroundImage: `url('${poster}')` }}></div>
            )}
            {isError && (
                <div className={styles.message}>
                    {message ? message : <FontIcon name="alert" size="x-large" md="x-large" color="red" />}
                </div>
            )}
        </div>
    );
};
